import * as Sentry from '@sentry/react';
import { Alert, Card } from 'antd';
import { includes, isEmpty } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router';
import { AppContext } from './AppContext';
import Error404 from './Error404';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import App from './app/App';
import useQueryWithCallbacks from './common/CommonUseQuery';
import HistoryRouter from './common/HistoryRouter';
import {
  ACCESS_TYPE,
  ALLOWED_ACTION_KEYS,
  ROUTES,
  SYSTEM_ROLES,
  TAB_KEYS,
} from './common/constants';
import { initGA } from './common/trackEvents';
import { useOnlineStatus } from './common/useNetworkDetect';
import CanPerform from './components/CanPerform';
import HasAccess from './components/HasAccess';
import LoaderComponent from './components/LoaderComponent';
import history from './historyData';
import ApproverLink from './modules/approver/ApproverLink';
import ApproverSuccess from './modules/approver/ApproverSuccess';
import ChangePassword from './modules/auth/ChangePassword';
import ForgotPassword from './modules/auth/ForgotPassword';
import Login from './modules/auth/Login';
import Logout from './modules/auth/Logout';
import Maintenance from './modules/auth/Maintenance';
import RefreshToken from './modules/auth/RefreshToken';
import ResetPassword from './modules/auth/ResetPassword';
import SetPassword from './modules/auth/SetPassword';
import Verify from './modules/auth/Verify';
import Dashboard from './modules/dashboard/Dashboard';
import SetUpEqcDetails from './modules/eqctype/components/eqcDetails/EqcDetails';
import Logs from './modules/logs/Logs';
import RedirectToTab from './modules/logs/components/RedirectToTab';
import Profile from './modules/profile/Profile';
import VerifyDetails from './modules/profile/VerifyDetails';
import AddProject from './modules/projects/AddProject';
import Projects from './modules/projects/Projects';
import EditProjectDetails from './modules/projects/components/EditProjectDetails';
import RedirectToAddProject from './modules/projects/components/RedirectToAddProject';
import RedirectToProjectDetails from './modules/projects/components/RedirectToProjectDetails';
import ProjectDetails from './modules/projects/components/projectDetails/ProjectDetails';
import UnassignAgency from './modules/projects/components/projectDetails/pages/agencies/UnassignAgency';
import EqcDetails from './modules/projects/components/projectDetails/pages/eqc/eqcDetails/EqcDetails';
import EqcTypeDetails from './modules/projects/components/projectDetails/pages/eqcTypes/eqcTypeDetails/EqcTypeDetails';
import InstructionDetails from './modules/projects/components/projectDetails/pages/instructions/instructionDetails';
import AddEditRegister from './modules/projects/components/projectDetails/pages/register/AddEditRegister';
import AddSetsProperty from './modules/projects/components/projectDetails/pages/register/AddSetsProperty';
import Register from './modules/projects/components/projectDetails/pages/register/Register';
import RegisterDetailsContent from './modules/projects/components/projectDetails/pages/register/RegisterDetailsContent';
import RfiDetails from './modules/projects/components/projectDetails/pages/rfi/RfiDetails';
import Setup from './modules/projects/components/projectDetails/pages/setup/Setup';
import InstructionType from './modules/projects/components/projectDetails/pages/setup/instructionType/InstructionType';
import RegisterApproverLink from './modules/registerApprover/RegisterApproverLink';
import RegisterApproverSuccess from './modules/registerApprover/RegisterApproverSuccess';
import Reports from './modules/report/Reports';
import ReportAssetsViewer from './modules/reportAssetsViewer/ReportAssetsViewer';
import Todo from './modules/todo/Todo';
import Details from './modules/todo/issues/details';
import ResponseSuccess from './modules/todo/issues/details/ResponseSuccess';
import { GET_PROFILE } from './modules/users/graphql/Queries';

const MyFallbackComponent = ({ error, componentStack }) => (
  <div>
    <p>
      <strong>Oops! An error occured!</strong>
    </p>
    <p>Below is the details…</p>
    <p>
      <strong>Error:</strong> {error.toString()}
    </p>
    <p>
      <strong>Stacktrace:</strong> {componentStack}
    </p>
  </div>
);

const RedirectToEqcType = ({ children }) => {
  const {
    state: {
      currentUser,
      currentUser: { roles },
    },
  } = useContext(AppContext);

  const hasInspectionAccess = !!HasAccess({ type: ACCESS_TYPE.INSPECTION });
  const hasInstructionAccess = !!HasAccess({ type: ACCESS_TYPE.INSTRUCTION });
  const hasRegisterAccess = !!HasAccess({ type: ACCESS_TYPE.REGISTER });

  if (isEmpty(currentUser)) {
    return <LoaderComponent />;
  }
  if (
    !includes(
      [SYSTEM_ROLES.SYSTEM_ADMIN, SYSTEM_ROLES.CHECKLIST_MAKER],
      roles?.[0],
    ) &&
    !currentUser?.projectUsers?.length > 0
  ) {
    if (hasInspectionAccess) {
      return <Navigate to={`${ROUTES.TODO}${ROUTES.EQC}`} />;
    }
    if (hasInstructionAccess) {
      return <Navigate to={`${ROUTES.TODO}${ROUTES.ISSUES}`} />;
    }
    if (hasRegisterAccess) {
      return <Navigate to={`${ROUTES.TODO}${ROUTES.REGISTER}`} />;
    }
    return <Navigate to={`${ROUTES.TODO}${ROUTES.EQC}`} />;
  }
  if (currentUser?.isLocal) {
    return <Navigate to={`${ROUTES.TODO}${ROUTES.ISSUES}`} />;
  }
  if (includes(roles, SYSTEM_ROLES.CHECKLIST_MAKER)) {
    if (hasInspectionAccess) {
      return <Navigate to={`${ROUTES.SETUP}${ROUTES.CHECKLISTS}`} />;
    }
    return (
      <div className="d-flex justify-center">
        <Alert
          className="max-width-500"
          title="Your organization doesn’t have access to the Inspection feature."
          description={
            <>
              <div>
                Your role only allows you to create checklists, which is part of
                the Inspection feature.
              </div>
              <div>Please contact your system admin to change your role.</div>
            </>
          }
          type="warning"
          showIcon
        />
      </div>
    );
  }
  if (hasInspectionAccess || hasInstructionAccess || hasRegisterAccess) {
    return children;
  }
  return <Navigate to={ROUTES.PROJECTS} />;
};

const RedirectFromMain = ({ children }) => {
  const location = useLocation();
  if (!location.state?.fromDashboardTab) {
    return <Navigate to={ROUTES.PROJECTS} replace />;
  }
  return children;
};

const RoutesCollection = () => {
  const {
    state: {
      currentUser: { currentRole },
    },
  } = useContext(AppContext);
  const hasInspectionAccess = !!HasAccess({ type: ACCESS_TYPE.INSPECTION });
  const hasInstructionAccess = !!HasAccess({ type: ACCESS_TYPE.INSTRUCTION });
  const hasRegisterAccess = !!HasAccess({ type: ACCESS_TYPE.REGISTER });

  const AUTH_MODULES = [
    {
      path: ROUTES.MAIN,
      element: <PublicRoute />,
      children: [
        { path: ROUTES?.FORGOT, element: <ForgotPassword /> },
        { path: ROUTES?.RESET, element: <ResetPassword /> },
        { path: ROUTES?.LOGIN, element: <Login /> },
        { path: ROUTES?.VERIFY, element: <Verify /> },
      ],
    },

    {
      path: ROUTES.MAIN,
      element: <PrivateRoute />,
      children: [
        { path: ROUTES?.LOGOUT, element: <Logout /> },
        { path: ROUTES?.AUTHENTICATION, element: <RefreshToken /> },
      ],
    },
    {
      path: ROUTES.MAIN,
      element: <Outlet />,
      children: [
        { path: ROUTES?.SET_PASSWORD, element: <SetPassword /> },
        { path: ROUTES?.USER_VERIFICATION, element: <VerifyDetails /> },
        { path: ROUTES?.REPORT_IMAGE_VIEWER, element: <ReportAssetsViewer /> },
      ],
    },
  ];

  const DASHBOARD_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            {
              path: ROUTES?.MAIN,
              element: (
                // ! Commented for future use
                // <RedirectToEqcType>
                <RedirectFromMain>
                  <Dashboard />
                </RedirectFromMain>
                // </RedirectToEqcType>
              ),
            },
          ],
        },
      ],
    },
  ];

  const PROJECT_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            {
              path: ROUTES.PROJECTS,
              element: (
                <RedirectToEqcType>
                  <Projects />
                </RedirectToEqcType>
              ),
            },
            {
              path: ROUTES.ADD_PROJECTS,
              element: <AddProject />,
            },
            {
              path: `${ROUTES.PROJECTS}/:projectId/edit`,
              element: <EditProjectDetails />,
            },
            ...(CanPerform({ action: ALLOWED_ACTION_KEYS.ADD_PROJECT })
              ? [
                  {
                    path: `${ROUTES.ADD_PROJECTS}/:projectId/:step`,
                    element: <AddProject />,
                  },
                ]
              : []),
            {
              path: `${ROUTES.ADD_PROJECTS}/:projectId`,
              element: <RedirectToAddProject />,
            },
            ...(HasAccess({ type: ACCESS_TYPE.INSPECTION })
              ? [
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.EQC}/:eqcId`,
                    element: <EqcDetails />,
                  },
                ]
              : []),
            ...(HasAccess({ type: ACCESS_TYPE.REGISTER })
              ? [
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.REGISTER}/create-control`,
                    element: <AddEditRegister />,
                  },
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.REGISTER}/:registerId/edit-control`,
                    element: <RegisterDetailsContent />,
                  },
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.REGISTER}/:registerId/add-set`,
                    element: <AddSetsProperty />,
                  },
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.REGISTER}/add-entry`,
                    element: <AddSetsProperty />,
                  },
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.REGISTER}/:registerId/:registerLogId`,
                    element: <RegisterDetailsContent />,
                  },
                ]
              : []),
            ...(HasAccess({ type: ACCESS_TYPE.INSPECTION })
              ? [
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.CHECKLISTS}/:eqcTypeId`,
                    element: <EqcTypeDetails />,
                  },
                ]
              : []),
            ...(HasAccess({ type: ACCESS_TYPE.INSPECTION })
              ? [
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.CHECKLISTS}/:eqcTypeId/draft`,
                    element: <EqcTypeDetails />,
                  },
                ]
              : []),
            ...(HasAccess({ type: ACCESS_TYPE.INSPECTION })
              ? [
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.CHECKLISTS}/:eqcTypeId/changelog`,
                    element: <EqcTypeDetails />,
                  },
                ]
              : []),
            ...(HasAccess({ type: ACCESS_TYPE.INSPECTION })
              ? [
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.RFI}/:rfiId`,
                    element: <RfiDetails />,
                  },
                ]
              : []),

            ...(HasAccess({ type: ACCESS_TYPE.INSPECTION })
              ? [
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.RFI}/:rfiId/draft`,
                    element: <RfiDetails />,
                  },
                ]
              : []),

            ...(HasAccess({ type: ACCESS_TYPE.INSPECTION })
              ? [
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.RFI}/:rfiId/changelog`,
                    element: <RfiDetails />,
                  },
                ]
              : []),
            {
              path: `${ROUTES.PROJECTS}/:projectId`,
              element: <RedirectToProjectDetails />,
            },
            ...(HasAccess({ type: ACCESS_TYPE.INSTRUCTION })
              ? [
                  {
                    path: `/:tenantId${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.INSTRUCTION}/:instructionId`,
                    element: <InstructionDetails />,
                  },
                  {
                    path: `${ROUTES.PROJECTS}/:projectId/${TAB_KEYS.INSTRUCTION}/:instructionId`,
                    element: <InstructionDetails />,
                  },
                ]
              : []),

            {
              path: `${ROUTES.PROJECTS}/:projectId/:tab/unassigned`,
              element: <ProjectDetails />,
            },
            {
              path: `${ROUTES.PROJECTS}/:projectId/:tab`,
              element: <ProjectDetails />,
              children: [
                {
                  path: `${ROUTES.PROJECTS}/:projectId/:tab/:registerId`,
                  element: <Register />,
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  const LOGS_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            { path: `${ROUTES.LOGS}/:tab`, element: <Logs /> },
            {
              path: ROUTES.LOGS,
              element: <RedirectToTab />,
            },
          ],
        },
      ],
    },
  ];

  const TODO_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            { path: `${ROUTES.TODO}/:tab`, element: <Todo /> },
            { path: `${ROUTES.TODO}/:tab/:id`, element: <Details /> },
            {
              path: `${ROUTES.TODO}/:tab/:id/success`,
              element: <ResponseSuccess />,
            },
          ],
        },
      ],
    },
  ];

  const REPORT_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [{ path: ROUTES.REPORTS, element: <Reports /> }],
        },
      ],
    },
  ];

  const USER_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            { path: ROUTES.PROFILE, element: <Profile /> },
            { path: ROUTES?.CHANGE, element: <ChangePassword /> },
          ],
        },
      ],
    },
  ];

  const APPROVER_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            { path: ROUTES.APPROVER_LINK, element: <ApproverLink /> },
            { path: ROUTES.APPROVER_SUCCESS, element: <ApproverSuccess /> },
            {
              path: ROUTES.REGISTER_APPROVER_LINK,
              element: <RegisterApproverLink />,
            },
            {
              path: ROUTES.REGISTER_APPROVER_SUCCESS,
              element: <RegisterApproverSuccess />,
            },
          ],
        },
      ],
    },
  ];

  const SETUP_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            { path: `${ROUTES.SETUP}/:tab`, element: <Setup /> },
            {
              path: `${ROUTES.SETUP}/${TAB_KEYS.INSTRUCTION_TYPE}`,
              element: (
                <div className="project-details">
                  <Card className="instruction-type-tab instruction-type-card">
                    <InstructionType />
                  </Card>
                </div>
              ),
            },
            ...(hasInspectionAccess &&
            CanPerform({ action: ALLOWED_ACTION_KEYS.VIEW_CHECKLISTS_PAGE })
              ? [
                  {
                    path: `${ROUTES.SETUP}${ROUTES.CHECKLISTS}/:eqcTypeId`,
                    element: <SetUpEqcDetails />,
                  },
                ]
              : []),
            ...(CanPerform({ action: ALLOWED_ACTION_KEYS.VIEW_AGENCY_PAGE })
              ? [
                  {
                    path: `${ROUTES.SETUP}${ROUTES.UNASSIGN_AGENCIES}`,
                    element: (
                      <Card>
                        <UnassignAgency isGlobal />
                      </Card>
                    ),
                  },
                ]
              : []),
          ],
        },
      ],
    },
  ];

  const OTHER_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            {
              path: '*',
              element: <Error404 />,
            },
          ],
        },
      ],
    },
  ];

  const routes = [
    ...DASHBOARD_MODULES,
    ...AUTH_MODULES,
    // ! Commented for future use
    // ...(CanPerform({ action: ALLOWED_ACTION_KEYS.VIEW_PROJECTS_PAGE })
    //   ? PROJECT_MODULES
    //   : []),
    ...PROJECT_MODULES,
    ...(CanPerform({ action: ALLOWED_ACTION_KEYS.VIEW_LOGS_PAGE })
      ? LOGS_MODULES
      : []),
    ...(hasInspectionAccess || hasInstructionAccess || hasRegisterAccess
      ? TODO_MODULES
      : []),
    ...((hasInspectionAccess || hasInstructionAccess) &&
    currentRole !== SYSTEM_ROLES.CHECKLIST_MAKER
      ? REPORT_MODULES
      : []),
    ...USER_MODULES,
    ...(CanPerform({ action: ALLOWED_ACTION_KEYS.VIEW_SETUP_PAGE })
      ? SETUP_MODULES
      : []),
    ...APPROVER_MODULES,
    ...OTHER_MODULES,
  ];
  const element = useRoutes(routes);
  return element;
};

const RoutesWrapper = () => {
  const { initializeAuth, getToken } = useContext(AppContext);
  const idToken = getToken();
  const isOnline = useOnlineStatus();
  const {
    state: { currentRole, projectRole, currentUser, oneTimeLogin },
    dispatch,
  } = useContext(AppContext);

  const { loading } = useQueryWithCallbacks(GET_PROFILE, {
    fetchPolicy: 'no-cache',
    // eslint-disable-next-line no-undef
    skip: !idToken || window?.location?.pathname === ROUTES.LOGOUT,
    onCompleted: (res) => {
      initializeAuth('', '', res?.getLoggedInUser);
      if (
        oneTimeLogin &&
        ((!res?.getLoggedInUser?.emailVerified &&
          res?.getLoggedInUser?.email) ||
          (!res?.getLoggedInUser?.numberVerified &&
            res?.getLoggedInUser?.phoneNo))
      ) {
        dispatch({ type: 'SET_SHOW_VERIFY_DETAILS_MODAL', data: true });
        dispatch({ type: 'ONE_TIME_LOGIN', data: false });
      }
    },
    onError() {},
  });

  useEffect(() => {
    initGA(process.env.REACT_APP_GA_CONFIG);
    // eslint-disable-next-line no-undef
  }, []);
  // use this variable from envs so that we can able to run maintenance page on runtime.
  const maintenance = process.env.REACT_APP_MAINTENANCE_ENABLE;
  if (
    // eslint-disable-next-line no-undef
    window?.location?.pathname !== ROUTES.LOGOUT &&
    (loading || (!isEmpty(currentUser) && !(currentRole || projectRole)))
  )
    return <LoaderComponent />;

  return (
    <>
      {!isOnline && (
        <div className="alert-div">
          <Alert
            title="Please check your network connection and try again."
            type="error"
            banner
          />
        </div>
      )}
      <Sentry.ErrorBoundary fallback={MyFallbackComponent}>
        {maintenance === 'true' ? (
          <Maintenance />
        ) : (
          <HistoryRouter history={history}>
            <RoutesCollection />
          </HistoryRouter>
        )}
      </Sentry.ErrorBoundary>
    </>
  );
};
export default RoutesWrapper;
